import { observable, action, computed, configure, runInAction } from 'mobx';
import { createContext, SyntheticEvent } from 'react';
import { IProject as IProject } from '../models/project';
import { toast } from 'react-toastify';
import agent from '../api/agent';
import { IProjectNote } from '../models/projectnote';
import { IProjectLog } from '../models/projectlog';
import { IProjectTask } from '../models/projecttask';
import { IProjectBog } from '../models/projectbog';
import { IProjectDocument } from '../models/projectdocument';

configure({enforceActions: 'always'});

class MinorStore {

  @observable projectRegistry = new Map();
  @observable projectSearchRegistry = new Map();
  @observable project: IProject | null = null;
  @observable projectnotes: IProjectNote[]  = [];
  @observable projectnote: IProjectNote | null = null;
  @observable projectlogRegistry = new Map();
  @observable technicianRegistry = new Map();
  @observable projecttaskRegistry = new Map();
  @observable projectbogRegistry = new Map();
  @observable projectdocumentRegistry = new Map();
  @observable activeDetailTab: number = 0;
  @observable activeListTab: number = 0;
  @observable loadingInitial = false;
  @observable submitting = false;
  @observable editMode = false;
  @observable target = '';


  @computed get projectsByDate() {
    return Array.from(this.projectSearchRegistry.values()).sort(
      (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)
    );
  }

  @computed get projectnotesByDate() {
    return this.projectnotes.sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
    );
  }

  @computed get projectlogsByDate() {
    return Array.from(this.projectlogRegistry.values()).sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
    );
  }

  @computed get projecttasksByTime() {
    return Array.from(this.projecttaskRegistry.values()).sort(
      (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)
    )
  }

  @computed get projectbogsByTime() {
    return Array.from(this.projectbogRegistry.values()).sort(
      (a, b) => Date.parse(a.bogDate) - Date.parse(b.bogDate)
    )
  }

  @computed get techniciansByName() {
    return Array.from(this.technicianRegistry.values()).sort(
      (a, b) => a.name.localeCompare(b.name))
    ;
  }

  @computed get projectdocumentsByName() {
    return Array.from(this.projectdocumentRegistry.values()).sort(
      (a, b) => a.id - b.id)
    ;
  }

  @action loadProjects = async (type: string, status: string) => {
    this.loadingInitial = true;
    try {
      const projects = await agent.Projects.list(type, status);
      runInAction('loading projects', () => {
        this.projectRegistry.clear();
        this.projectSearchRegistry.clear();
        projects.forEach(project => {
          (project.receiveDate) ? project.receiveDate = project.receiveDate.substr(0,10) : project.receiveDate = "";
          (project.requireDate) ? project.requireDate = project.requireDate.substr(0,10) : project.requireDate = "";
         (project.jobStartDate) ? project.jobStartDate = project.jobStartDate.substr(0,10) : project.jobStartDate = "";
         (project.estimatedCompletionDate) ? project.estimatedCompletionDate = project.estimatedCompletionDate.substr(0,10) : project.estimatedCompletionDate = "";
          this.projectRegistry.set(project.id, project);
          this.projectSearchRegistry.set(project.id, project);
        });
        this.loadingInitial = false;
        
      })

    } catch (error) {
      runInAction('load projects error', () => {
        this.loadingInitial = false;
      })
      toast.error('load projects error');
    }
  };

  @action loadProject = async (id: string) => {
    let project = this.getProject(id);
    if (project) {
      this.project = project;
    } else {
      this.loadingInitial = true;
       try {
         project = await agent.Projects.details(id);
         runInAction('getting project',() => {
           this.project = project;
           this.loadingInitial = false;
         })
       } catch (error) {
         runInAction('get project error', () => {
           this.loadingInitial = false;
         })
         toast.error('get project error');
         console.log(error);
       }
    }
  };

  @action clearProject = () => {
    this.project = null;
  }

  getProject = (id: string) => {
    return this.projectRegistry.get(Number(id));
  }


  @action createProject = async (project: IProject) => {
    this.submitting = true;
    try {
     
      project.createdAt = new Date().toJSON();
      project.updatedAt = new Date().toJSON();

      await agent.Projects.create(project);
      runInAction('create project', () => {
        // this.projectRegistry.set(project.id, project);
        this.submitting = false;
      })
    } catch (error) {
      runInAction('create project error', () => {
        this.submitting = false;
      })
      toast.error('create project error');
      console.log(error);
    }
  };

  @action editProject = async (project: IProject) => {
    this.submitting = true;
    try {
     // project.updatedAt = new Date().toJSON();
      await agent.Projects.update(project);
      runInAction('editing project', () => {
        this.projectRegistry.set(project.id, project);
        this.projectSearchRegistry.set(project.id, project);
        this.project = project;
        this.submitting = false;
      })

    } catch (error) {
      runInAction('edit project error', () => {
        this.submitting = false;
      })
      toast.error('edit project error');
      console.log(error);
    }
  };

  @action deleteProject = async (id: string) => {
    this.submitting = true;
    this.target = id;
    try {
      await agent.Projects.delete(id);
      runInAction('deleting project', () => {
        this.projectRegistry.delete(id);
        this.projectSearchRegistry.delete(id);
        this.submitting = false;
        this.target = '';
      })
    } catch (error) {
      runInAction('delete project error', () => {
        this.submitting = false;
        this.target = '';
      })
      toast.error('delete project error');
      console.log(error);
    }
  }

  @action generate = async () => {
    let code = "";
    try {
      var project = await agent.Projects.generate();
      code = project.projectCode;
      runInAction("getting code", () => {
        return code;
      });
    } catch (error) {
      runInAction("get code error", () => {});
      toast.error("get code error");
      console.log(error);
    }
    return code;
  };

  //Project Log Tab
  @action loadProjectLogs = async (projectId: string) => {
    try {
      const projectlogs = await agent.ProjectLogs.list(projectId);
      this.projectlogRegistry.clear();
      runInAction('loading tasks', () => {
        projectlogs.forEach(projectlog => {
          this.projectlogRegistry.set(projectlog.id, projectlog);
        });
      })
  
    } catch (error) {
      runInAction('load tasks error', () => {
      })
      toast.error('load tasks error');
    }
  };
  
  @action createProjectLog = async (projectlog: IProjectLog) => {
    this.submitting = true;
    try {
     projectlog.createdAt = new Date().toJSON();
     projectlog.updatedAt = new Date().toJSON();
  
      await agent.ProjectLogs.create(projectlog);
      runInAction('create project log', () => {
        this.submitting = false;
      })
    } catch (error) {
      runInAction('create project log error', () => {
        this.submitting = false;
      })
      toast.error('create project log error');
      console.log(error);
    }
  };
  
  @action deleteProjectLog = async (event: SyntheticEvent<HTMLButtonElement>, id: string) => {
    this.target = event.currentTarget.name;
    try {
      await agent.ProjectLogs.delete(id);
      runInAction('deleting project', () => {
        this.projectlogRegistry.delete(id);
        this.target = '';
      })
    } catch (error) {
      runInAction('delete project error', () => {
        this.target = '';
      })
      toast.error('delete project error');
      console.log(error);
    }
  }

  //Project Notes
  @action loadProjectNotes = async (projectId: string) => {
    // this.loadingInitial = true;
    try {
      this.projectnotes.length = 0;
      const projectnotes = await agent.ProjectNotes.list(projectId);
      runInAction('loading tasks', () => {
        projectnotes.forEach(projectnote => {
          this.projectnotes.push(projectnote);
        });
      // this.loadingInitial = false;
       })
  
    } catch (error) {
      runInAction('load project notes error', () => {
        // this.loadingInitial = false;
      })
      toast.error('load project notes error');
    }
  };
  
  @action createProjectNote = async (projectnote: IProjectNote) => {
    this.submitting = true;
    try {
      const note = await agent.ProjectNotes.create(projectnote);
      runInAction('create project note', () => {
        // this.projectnoteRegistry.set(projectnote.id, projectnote);
        this.projectnotes.push(note);
        this.submitting = false;
      })
    } catch (error) {
      runInAction('create project note error', () => {
        this.submitting = false;
      })
      toast.error('create project note error');
      console.log(error);
    }
  };
  
  @action deleteProjectNote = async (note: IProjectNote) => {
    try {
      await agent.ProjectNotes.delete(note.id);
      runInAction('deleting project note', () => {
        let index = this.projectnotes.indexOf(note);
        if (index !== -1) {
          this.projectnotes.splice(index, 1);
        }
        this.target = '';
      })
    } catch (error) {
      runInAction('delete project note error', () => {
        this.target = '';
      })
      toast.error('delete project note error');
      console.log(error);
    }
  }

  @action updateProjectNote = async (id: string, status: string) => {
    this.submitting = true;
    try {
      const note = await agent.ProjectNotes.update(id, status);
      runInAction('create project note', () => {
        let objIndex = this.projectnotes.findIndex((obj => obj.id == id));
        this.projectnotes[objIndex].status = status;
        this.submitting = false;
      })
    } catch (error) {
      runInAction('create project note error', () => {
        this.submitting = false;
      })
      toast.error('create project note error');
      console.log(error);
    }
  };  

  @action selectMinorTitle = async (title: string, text: string) => {
    let projects = Array.from(this.projectRegistry.values());
    if (text !== 'ALL') {
      let selectedProjects = [];
      if (title === 'workType')
      {
        selectedProjects = projects.filter((project) =>
        project.workType.toLowerCase() === text.toLowerCase()
        );
      }
      else if (title === 'customerId')
      {
        selectedProjects = projects.filter((project) =>
        project.customerId === text
        );
      }
      else if (title === 'category')
      {
        selectedProjects = projects.filter((project) =>
        project.category.toLowerCase() === text.toLowerCase()
        );
      }
      else if (title === 'status')
      {
        selectedProjects = projects.filter((project) =>
        project.status.toLowerCase() === text.toLowerCase()
        );
      }
      this.projectSearchRegistry.clear();
         selectedProjects.forEach((project) => {
          this.projectSearchRegistry.set(project.id, project);
        });
    } else {
      this.projectSearchRegistry.clear();
      projects.forEach((project) => {
        this.projectSearchRegistry.set(project.id, project);
      });
    }
  };

  //Task
  @action loadProjectTasks = async (projectId: string) => {
    try {
      const projecttasks = await agent.MinorTasks.list(projectId);
      this.projecttaskRegistry.clear();
      runInAction('loading tasks', () => {
        if (projecttasks.length === 0)
        {
          this.projecttaskRegistry.set('new', {createdAt: new Date().toISOString(), itemCategory: 'new', currentValue: 0, paidValue: 0});
        }
        else
        {
          projecttasks.forEach(projecttask => {
            projecttask.itemDescription = projecttask.itemDescription.split(',');
            this.projecttaskRegistry.set(projecttask.id, projecttask);
          });
        };
      })
  
    } catch (error) {
      runInAction('load tasks error', () => {
      })
      toast.error('load tasks error');
    }
  };
  
  @action createProjectTask = async (projecttask: IProjectTask) => {
    this.submitting = true;
    projecttask.itemDescription = projecttask.itemDescription.toString();
    try {
      const task = await agent.MinorTasks.create(projecttask);
      runInAction('create project task', () => {
        this.projecttaskRegistry.delete('new');
        task.itemDescription = task.itemDescription.split(',');
        this.projecttaskRegistry.set(task.id, task);
        this.submitting = false;
      })
    } catch (error) {
      runInAction('create project task error', () => {
        this.submitting = false;
      })
      toast.error('create project task error');
      console.log(error);
    }
  };
  
  @action deleteProjectTask = async (id: string) => {
    try {
      await agent.MinorTasks.delete(id);
      runInAction('deleting project task', () => {
        this.projecttaskRegistry.delete(id);
      })
    } catch (error) {
      runInAction('delete project task error', () => {
      })
      toast.error('delete project task error');
      console.log(error);
    }
  }

  @action addProjectTask = async () => {
    this.projecttaskRegistry.set('new', {createdAt: new Date().toISOString(), itemCategory: 'new', currentValue: 0});
  }

  @action updateTask = async (task : IProjectTask, name: keyof IProjectTask, value: any) => {
    task[name] = value as never;
    task['currentValue'] = task.unitRate * task.orderQty + Number(task.claimedQty);
    this.projecttaskRegistry.set(task.id, task);
  };

  @action editProjectTask = async (projecttask: IProjectTask) => {
    this.submitting = true;
    projecttask.itemDescription = projecttask.itemDescription.toString();
    try {
      await agent.MinorTasks.update(projecttask);
      runInAction('editing project', () => {
        projecttask.itemDescription = projecttask.itemDescription.split(',');
        this.projecttaskRegistry.set(projecttask.id, projecttask);
        this.submitting = false;
      })
      toast.success('Saved successfully');

    } catch (error) {
      runInAction('edit project error', () => {
        this.submitting = false;
      })
      toast.error('edit project error');
      console.log(error);
    }
  };

  //BOG
  @action loadProjectBogs = async (projectId: string) => {
    try {
      const projectbogs = await agent.ProjectBogs.list(projectId);
      this.projectbogRegistry.clear();
      runInAction('loading bogs', () => {
        if (projectbogs.length === 0)
        {
          this.projectbogRegistry.set('new', {createdAt: new Date().toISOString(), activity: 'new'});
        }
        else
        {
          projectbogs.forEach(projectbog => {
            projectbog.bogDate = projectbog.bogDate.substr(0,10);
            projectbog.technicianEmail = projectbog.technicianEmail.split(',');
            this.projectbogRegistry.set(projectbog.id, projectbog);
          });
        };
      })
  
    } catch (error) {
      runInAction('load bogs error', () => {
      })
      toast.error('load bogs error');
    }
  };
  
  @action createProjectBog = async (projectbog: IProjectBog) => {
    this.submitting = true;
    projectbog.technicianEmail = projectbog.technicianEmail.toString();
    try {
      const bog = await agent.ProjectBogs.create(projectbog);
      runInAction('create project bog', () => {
        this.projectbogRegistry.delete('new');
        bog.bogDate = bog.bogDate.substr(0,10)
        bog.technicianEmail = bog.technicianEmail.split(',');
        this.projectbogRegistry.set(bog.id, bog);
        this.submitting = false;
      })
    } catch (error) {
      runInAction('create project bog error', () => {
        this.submitting = false;
      })
      toast.error('create project bog error');
      console.log(error);
    }
  };
  
  @action deleteProjectBog = async (id: string) => {
    try {
      await agent.ProjectBogs.delete(id);
      runInAction('deleting project bog', () => {
        this.projectbogRegistry.delete(id);
      })
    } catch (error) {
      runInAction('delete project bog error', () => {
      })
      toast.error('delete project bog error');
      console.log(error);
    }
  }

  @action addProjectBog = async () => {
    this.projectbogRegistry.set('new', {createdAt: new Date().toISOString(), activity: 'new'});
  }

  @action updateBog = async (task : IProjectBog, name: keyof IProjectBog, value: any) => {
    task[name] = value as never;
    this.projectbogRegistry.set(task.id, task);
  };

  @action editProjectBog = async (projectbog: IProjectBog) => {
    this.submitting = true;
    projectbog.technicianEmail = projectbog.technicianEmail.toString();
    try {
      await agent.ProjectBogs.update(projectbog);
      runInAction('editing project', () => {
        projectbog.technicianEmail = projectbog.technicianEmail.split(',');
        this.projectbogRegistry.set(projectbog.id, projectbog);
        this.submitting = false;
      })
      toast.success('Saved successfully');

    } catch (error) {
      runInAction('edit project bog error', () => {
        this.submitting = false;
      })
      toast.error('edit project bog error');
      console.log(error);
    }
  };
  
  //technician
  @action loadTechnicians = async () => {
    try {
      const employees = await agent.Employees.list();
      runInAction("loading empolyees", () => {
        this.technicianRegistry.clear();
        employees.forEach((employee) => {
          if ((employee.status === 'Subcontractor' || employee.status === 'Employee') && 
          employee.projectType.includes('Minor')) 
          {
            this.technicianRegistry.set(employee.email, employee);
          }
        });
      });
    } catch (error) {
      runInAction("load technician Error", () => {
      });
      toast.error("load technician Error");
    }
  };

    //Project Design Tab
  @action loadProjectDocuments = async (projectId: string) => {
      try {
        const projectdocuments = await agent.ProjectDocuments.list(projectId);
        this.projectdocumentRegistry.clear();
        runInAction('loading documents', () => {
          projectdocuments.forEach(projectdocument => {
            this.projectdocumentRegistry.set(projectdocument.id, projectdocument);
          });
        })
    
      } catch (error) {
        runInAction('load documents error', () => {
        })
        toast.error('load documents error');
      }
  };
    
  @action createProjectDocuments  = async (projectdocuments: IProjectDocument) => {
      this.submitting = true;
      try {
        const docs = await agent.ProjectDocuments.create(projectdocuments);
        runInAction('create documents', () => {
          this.projectdocumentRegistry.set(docs.id, docs);
          this.submitting = false;
        })
      } catch (error) {
        runInAction('create documents error', () => {
          this.submitting = false;
        })
        toast.error('create documents error');
        console.log(error);
      }
  };
    
  @action deleteProjectDocument = async (event: SyntheticEvent<HTMLButtonElement>, id: string) => {
      this.target = event.currentTarget.name;
      try {
        await agent.ProjectDocuments.delete(id);
        runInAction('deleting project', () => {
          this.projectdocumentRegistry.delete(id);
          this.target = '';
        })
      } catch (error) {
        runInAction('delete project error', () => {
          this.target = '';
        })
        toast.error('delete project error');
        console.log(error);
      }
  }

  @action searchProject = async (term: string) => {
    let projects = Array.from(this.projectRegistry.values());
    if (term) {
      const selectedProjects = projects.filter((project) =>
        project.projectCode.toLowerCase().includes(term.toLowerCase()) || 
        project.address.toLowerCase().includes(term.toLowerCase())
      );
      this.projectSearchRegistry.clear();
        selectedProjects.forEach((project) => {
          this.projectSearchRegistry.set(project.id, project);
        });
    } else {
      this.projectSearchRegistry.clear();
      projects.forEach((project) => {
         this.projectSearchRegistry.set(project.id, project);
      });
    }
  };

  @action copyTechnicians = () => {
      let techs: any;
      this.projectbogsByTime.forEach((bog, index) => {
         if (index === 0) techs = bog.technicianEmail
         else bog.technicianEmail = techs;
      })
  } 

  @action setActiveDetailTab = (activeIndex: number) => {
    this.activeDetailTab = activeIndex;
  } 

  @action setActiveListTab = (activeIndex: number) => {
    this.activeListTab = activeIndex;
  } 

  @action clearPage = () => {
    this.projectlogRegistry.clear();
    this.technicianRegistry.clear();
    this.projecttaskRegistry.clear();
    this.projectbogRegistry.clear();
    this.project = null;
    this.projectnotes.length = 0;
    this.projectnote = null;
  }

}

export default createContext(new MinorStore());
